import { hash } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { TaskInstance } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, or } from 'ember-truth-helpers';
import GeneralTrackingTrackUsageAnalyticsHoverWrapper from 'lumen/components/general/tracking/track-usage-analytics-hover-wrapper';
import hasFeature from 'lumen/helpers/has-feature';
import type { PaPmsInfo, PaSubscriptionMetaInfo, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { PA_PMS_RATE_REFRESH_TYPE } from 'lumen/utils/constants/pricing-assistant-constants';
import { getUpdateTimestampFromExtractDatetimes } from 'lumen/utils/get-update-timestamp-from-extract-datetimes';

import styles from './data-sync-status.module.css';

interface HotelPricingAssistantDataSyncStatusSignature {
  Args: {
    arePageRequestsLoading: boolean | undefined;
    currentSelectedMonth: Dayjs;
    hasLiveShop: boolean;
    periods: Period[];
    requestPricingAssistantPmsInfoTaskResult?: TaskInstance<PaPmsInfo | null>;
    requestPricingAssistantSubscriptionMetaTaskResult: TaskInstance<PaSubscriptionMetaInfo | null>;
  };
}

export default class HotelPricingAssistantDataSyncStatus extends Component<HotelPricingAssistantDataSyncStatusSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitors() {
    return this.hotelAndMarketService.activeCompetitors || [];
  }

  // Compset rates update timestamp derived from the extract datetimes of the competitor hotel carates from start of selected month or today (if selected month is current month) until the end of the selected month
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetRatesUpdateTimestampFromExtractDatetimes() {
    const startOfDay = dayjs.utc().startOf('day');
    const endOfCurrentSelectedMonth = dayjs
      .utc(this.args.currentSelectedMonth)
      .endOf('month');

    // Array of arrays of competitor rates unix extract_datetimes per period
    const competitorsExtractDatetimesUnix: number[][] = [];
    let updateTimestamp = null;

    this.args.periods?.forEach((period) => {
      const competitorsExtractDatetimesUnixForPeriod: number[] = [];

      // Only take into account periods from today until the end of the month
      if (
        period.fromDate.isSameOrAfter(startOfDay, 'day') &&
        period.fromDate.isSameOrBefore(endOfCurrentSelectedMonth, 'day')
      ) {
        this.competitors.forEach((competitor) => {
          const competitorRateExtractDatetime =
            this.getExtractDatetimeForHotelinfoId(
              period,
              // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
              competitor.id,
            );

          if (competitorRateExtractDatetime > 0) {
            competitorsExtractDatetimesUnixForPeriod.push(
              competitorRateExtractDatetime,
            );
          }
        });
      }

      if (competitorsExtractDatetimesUnixForPeriod.length) {
        competitorsExtractDatetimesUnix.push(
          competitorsExtractDatetimesUnixForPeriod,
        );
      }
    });

    if (competitorsExtractDatetimesUnix.length) {
      updateTimestamp = getUpdateTimestampFromExtractDatetimes(
        competitorsExtractDatetimesUnix,
      );
    }

    return updateTimestamp;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCompsetRatesUpdateTimestamp() {
    if (this.compsetRatesUpdateTimestampFromExtractDatetimes) {
      return this.compsetRatesUpdateTimestampFromExtractDatetimes.fromNow();
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOtbUpdateTimestamp() {
    if (this.otbUpdateTimestamp) {
      return this.otbUpdateTimestamp.fromNow();
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOwnHotelAndCompsetRatesUpdateTimestamp() {
    // Case 1: PMS is integrated
    /// Case 1.1: PMS rate refresh type is real-time -> show 'Just now'
    /// Case 1.2: PMS rate refresh type is scheduled -> show last rate refresh timestamp
    /// Case 1.3: PMS rate refresh type is no refresh -> show own hotel booking.com rates update timestamp
    if (this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated) {
      if (this.pmsRateRefreshType) {
        if (this.pmsRateRefreshType === PA_PMS_RATE_REFRESH_TYPE.REALTIME) {
          return this.intl.t(
            'pricing_assistant.data_sync_status.my_rates.just_now',
          );
        }

        if (
          this.paPmsInfo &&
          this.paPmsInfo.pricingAssistantLastRateRefresh &&
          (this.pmsRateRefreshType === PA_PMS_RATE_REFRESH_TYPE.SCHEDULED ||
            this.pmsRateRefreshType === PA_PMS_RATE_REFRESH_TYPE.REFRESHABLE)
        ) {
          return this.paPmsInfo.pricingAssistantLastRateRefresh.fromNow();
        }

        if (this.pmsRateRefreshType === PA_PMS_RATE_REFRESH_TYPE.NO_REFRESH) {
          return this
            .formattedOwnHotelAndCompsetRatesUpdateTimestampFromExtactDatetimes;
        }
      }
    } else {
      // Case 2: PMS is not integrated -> show own hotel and compset booking.com rates update timestamp
      return this
        .formattedOwnHotelAndCompsetRatesUpdateTimestampFromExtactDatetimes;
    }

    return this.intl.t('general.missing');
  }

  // My rates update timestamp derived from the extract datetimes of the own hotel and competitor carates from start of selected month or today (if selected month is current month) until the end of the selected month
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOwnHotelAndCompsetRatesUpdateTimestampFromExtactDatetimes() {
    if (this.ownHotelAndCompsetRatesUpdateTimestampFromExtactDatetimes) {
      return this.ownHotelAndCompsetRatesUpdateTimestampFromExtactDatetimes.fromNow();
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRecommendationUpdateTimestamp() {
    if (
      this.paSubscriptionMetaInfo &&
      this.paSubscriptionMetaInfo.lastRecommendationsRefresh
    ) {
      return this.paSubscriptionMetaInfo.lastRecommendationsRefresh.fromNow();
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLiveshopActive() {
    return this.liveupdate?.isPolling;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get monthsOffsetFromToday() {
    return this.args.currentSelectedMonth.diff(
      this.hotelAndMarketService.getLocalTime().startOf('month'),
      'months',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbUpdateTimestamp() {
    // Note: don't use pmsIntegrationLastUpdate here as this doesn't include live OTB.
    if (this.hotelAndMarketService.hotel.lastOtbUpdateIncludingLiveOtb) {
      return this.hotelAndMarketService.hotel.lastOtbUpdateIncludingLiveOtb;
    }

    return null;
  }

  // Same logic as Rates mode update timestamp
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelAndCompsetRatesUpdateTimestampFromExtactDatetimes() {
    const ownHotelinfoId = this.hotelAndMarketService.hotel.hotelinfo.id;
    const startOfDay = dayjs.utc().startOf('day');
    const endOfCurrentSelectedMonth = dayjs
      .utc(this.args.currentSelectedMonth)
      .endOf('month');
    const oneHourAgoInUnix = dayjs.utc().subtract(1, 'hour').unix();

    // Array of arrays of unix extract_datetimes per period
    const extractDatetimesUnix: number[][] = [];
    let updateTimestamp = null;

    this.args.periods?.forEach((period) => {
      const extractDatetimesUnixForPeriod: number[] = [];

      // Only take into account periods from today until the end of the month
      if (
        period.fromDate.isSameOrAfter(startOfDay, 'day') &&
        period.fromDate.isSameOrBefore(endOfCurrentSelectedMonth, 'day')
      ) {
        const ownHotelRateExtractDatetime =
          this.getExtractDatetimeForHotelinfoId(
            period,
            // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
            ownHotelinfoId,
          );

        if (ownHotelRateExtractDatetime > 0) {
          extractDatetimesUnixForPeriod.push(ownHotelRateExtractDatetime);
        }

        // If the own hotel has been updated in the past hour, we stop here and only look at the extract_datetime of the own hotel
        if (ownHotelRateExtractDatetime < oneHourAgoInUnix) {
          // If not: we go over the competitors and add their extract_datetimes to the updateTimesUnix array
          this.competitors.forEach((competitor) => {
            const competitorRateExtractDatetime =
              this.getExtractDatetimeForHotelinfoId(
                period,
                // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
                competitor.id,
              );

            if (competitorRateExtractDatetime > 0) {
              extractDatetimesUnixForPeriod.push(competitorRateExtractDatetime);
            }
          });
        }
      }

      if (extractDatetimesUnixForPeriod.length) {
        extractDatetimesUnix.push(extractDatetimesUnixForPeriod);
      }
    });

    if (extractDatetimesUnix.length) {
      updateTimestamp =
        getUpdateTimestampFromExtractDatetimes(extractDatetimesUnix);
    }

    return updateTimestamp;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paPmsInfo() {
    return this.args.requestPricingAssistantPmsInfoTaskResult?.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get paSubscriptionMetaInfo() {
    return this.args.requestPricingAssistantSubscriptionMetaTaskResult.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsRateRefreshType() {
    if (this.paPmsInfo) {
      return this.paPmsInfo.pricingAssistantRateRefreshType;
    }

    return null;
  }

  // Helper function to get the extract datetime for a specific hotelinfoId from a period
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getExtractDatetimeForHotelinfoId(period: Period, hotelinfoId: string) {
    const caratesObject = period.carates.filter(
      (carate) => carate.hotelinfo.id === hotelinfoId,
    )[0];

    return dayjs.utc(caratesObject?.extractDatetime).unix();
  }

  <template>
    <GeneralTrackingTrackUsageAnalyticsHoverWrapper
      @eventName="Data Freshness Checked"
      @properties={{hash
        eventTrigger="calendar-view"
        monthsOffsetFromToday=this.monthsOffsetFromToday
      }}
    >
      <div class="{{styles.container}} flex flex-row items-center gap-100">
        {{#unless @arePageRequestsLoading}}
          <PrismIcon @name="circle-information-outline" @size="small" />
          <span class="text-300--regular">{{t
              "pricing_assistant.data_sync_status.label"
            }}</span>
          <EmberTooltip @side="bottom" @tooltipClass="default-tooltip">
            <table>
              <tbody>
                {{! Show compset's rate timestamp seperate only if PMS integrated }}
                {{#if
                  this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated
                }}
                  {{! Compset's rates timetamp }}
                  <tr class="{{styles.row}} flex flex-row">
                    <td
                      class="{{styles.label-column}}
                        text-200--medium flex flex-row items-center"
                    >
                      {{t
                        "pricing_assistant.data_sync_status.competitor_rates"
                      }}

                      {{#if (and @hasLiveShop this.isLiveshopActive)}}
                        <PrismIcon
                          @name="sync"
                          @size="extra-small"
                          class="ml-100 {{if this.isLiveshopActive 'spinning'}}"
                        />
                      {{/if}}
                    </td>
                    <td
                      class="text-200--bold"
                    >{{this.formattedCompsetRatesUpdateTimestamp}}</td>
                  </tr>
                {{/if}}

                {{! Rates timestamp }}
                <tr class="{{styles.row}} flex flex-row">
                  <td
                    class="{{styles.label-column}}
                      text-200--medium flex flex-row items-center"
                  >
                    {{#if
                      this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated
                    }}
                      {{t "pricing_assistant.data_sync_status.my_rates"}}
                      ({{t "general.pms"}})
                    {{else}}
                      {{t "pricing_assistant.settings.section.rates.header"}}
                    {{/if}}

                    {{! Show own rates liveshop icon only if PMS not integrated }}
                    {{#unless
                      this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated
                    }}
                      {{#if (and @hasLiveShop this.isLiveshopActive)}}
                        <PrismIcon
                          @name="sync"
                          @size="extra-small"
                          class="ml-100 {{if this.isLiveshopActive 'spinning'}}"
                        />
                      {{/if}}
                    {{/unless}}
                  </td>
                  <td
                    class="text-200--bold"
                  >{{this.formattedOwnHotelAndCompsetRatesUpdateTimestamp}}</td>
                </tr>

                {{! OTB timestamp: Show the row in case OTB integrated or integration is available }}
                {{#if
                  (or
                    (hasFeature "pms.otb.view")
                    this.pricingAssistantPmsSs.arePricingAssistantPMSSelfServiceOTBPlaceholdersVisible
                  )
                }}
                  <tr class={{styles.row}}>
                    <td
                      class="{{styles.label-column}}
                        text-200--medium
                        {{if
                          this.pricingAssistantPmsSs.showPricingAssistantPMSSelfServiceOTBPlaceholdersAsUpsellText
                          'text-brand font-bold'
                        }}"
                    >{{t "pricing_assistant.my_OTB"}}</td>
                    <td
                      class="text-200--bold
                        {{if
                          this.pricingAssistantPmsSs.showPricingAssistantPMSSelfServiceOTBPlaceholdersAsUpsellText
                          'text-brand font-bold'
                        }}"
                    >
                      {{this.formattedOtbUpdateTimestamp}}
                    </td>
                  </tr>
                {{/if}}

                {{! Recommendations timestamp }}
                <tr class={{styles.row}}>
                  <td class="{{styles.label-column}} text-200--medium">{{t
                      "pricing_assistant.data_sync_status.recommendations"
                    }}</td>
                  <td
                    class="text-200--bold"
                  >{{this.formattedRecommendationUpdateTimestamp}}</td>
                </tr>
              </tbody>
            </table>
          </EmberTooltip>
        {{/unless}}
      </div>
    </GeneralTrackingTrackUsageAnalyticsHoverWrapper>
  </template>
}
